import { z } from 'zod';
import {
  BOARD_TITLE_MAX_LENGTH,
  CARD_TITLE_MAX_LENGTH,
  SHORT_LINK_MAX_LENGTH,
  cardNumberSchema,
  cardReferenceSchema,
} from './board';
import { ulidSchema } from './id';
import { LIST_TITLE_MAX_LENGTH } from './list';
import { RANK_MAX_LENGTH } from './rank';

/**
 * Planner — B75 shared backend contracts for `FR-NOTIF-020` through
 * `FR-NOTIF-025`.
 *
 * Planner is a personal surface that composes board cards, Inbox cards, local
 * focus blocks and read-only external calendar events. These schemas are
 * route-specific and deliberately do not widen the shipped card, Inbox or
 * Calendar View contracts.
 */

export const PLANNER_DEFAULT_FOCUS_DURATION_MINUTES = 60;
export const DEFAULT_FOCUS_BLOCK_DURATION_MINUTES =
  PLANNER_DEFAULT_FOCUS_DURATION_MINUTES;
export const PLANNER_MIN_FOCUS_DURATION_MINUTES = 15;
export const PLANNER_MAX_FOCUS_DURATION_MINUTES = 8 * 60;
export const PLANNER_CONNECTED_ACCOUNT_LIMIT = 10;
export const PLANNER_SUGGESTION_LIMIT_DEFAULT = 5;
export const PLANNER_SUGGESTION_LIMIT_MAX = 20;
/**
 * `FR-NOTIF-025` source-board constraint: the most boards one proposal request
 * may name. This is a bound on the size of the `IN` list the suggestion query
 * builds, not a permission check -- naming a board only ever *narrows* a set
 * that `isBoardReadableBy(userId)` has already bounded, so an over-long list is
 * a denial-of-service risk and never a disclosure one.
 */
export const PLANNER_SUGGESTION_SOURCE_BOARD_LIMIT = 100;
export const PLANNER_FOCUS_BLOCK_CARD_LIMIT = 20;

export const PLANNER_EXTERNAL_REF_MAX_LENGTH = 1024;
export const PLANNER_CALENDAR_NAME_MAX_LENGTH = 255;
export const PLANNER_CALENDAR_COLOUR_MAX_LENGTH = 64;
export const PLANNER_EXTERNAL_EVENT_TITLE_MAX_LENGTH = 1024;
export const PLANNER_EXTERNAL_EVENT_LOCATION_MAX_LENGTH = 1024;
export const PLANNER_FOCUS_SYNC_ERROR_MAX_LENGTH = 1024;
export const PLANNER_SUGGESTION_REASON_MAX_LENGTH = 512;

const plannerInstantSchema = z.iso.datetime({ offset: true });
const plannerTimestampSchema = z.string().min(1).max(64);
const plannerTimeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/);

const increasingRangeIssue = {
  message: 'from must be before to',
  path: ['to'],
};

function isIncreasingRange(value: { from: string; to: string }): boolean {
  return Date.parse(value.from) < Date.parse(value.to);
}

function isIncreasingBlock(value: { startAt: string; endAt: string }): boolean {
  return Date.parse(value.startAt) < Date.parse(value.endAt);
}

function hasUniqueValues(values: readonly string[]): boolean {
  return new Set(values).size === values.length;
}

function timeParts(value: string): [number, number, number] {
  const [hours = '0', minutes = '0', seconds = '0'] = value.split(':');
  return [Number(hours), Number(minutes), Number(seconds)];
}

function secondsSinceMidnight(value: string): number {
  const [hours, minutes, seconds] = timeParts(value);
  return hours * 3600 + minutes * 60 + seconds;
}

function isIncreasingTimeRange(value: { start: string; end: string }): boolean {
  return secondsSinceMidnight(value.start) < secondsSinceMidnight(value.end);
}

export const plannerRangeQuerySchema = z
  .strictObject({
    from: plannerInstantSchema,
    to: plannerInstantSchema,
  })
  .refine(isIncreasingRange, increasingRangeIssue)
  .describe('Planner visible range using half-open [from, to) semantics.');

export type PlannerRangeQuery = z.infer<typeof plannerRangeQuerySchema>;

export const plannerWeekdaySchema = z.enum([
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]);

export type PlannerWeekday = z.infer<typeof plannerWeekdaySchema>;

export const plannerWorkingDaysSchema = z
  .array(plannerWeekdaySchema)
  .min(1)
  .max(7)
  .refine(hasUniqueValues, { message: 'workingDays must not contain duplicates' });

export type PlannerWorkingDays = z.infer<typeof plannerWorkingDaysSchema>;

export const plannerWorkingHoursSchema = z
  .strictObject({
    start: plannerTimeOfDaySchema,
    end: plannerTimeOfDaySchema,
  })
  .refine(isIncreasingTimeRange, {
    message: 'start must be before end',
    path: ['end'],
  });

export type PlannerWorkingHours = z.infer<typeof plannerWorkingHoursSchema>;

export const plannerPreferencesSchema = z.strictObject({
  includeUnassigned: z.boolean(),
  workingHours: plannerWorkingHoursSchema,
  workingDays: plannerWorkingDaysSchema,
});

export type PlannerPreferences = z.infer<typeof plannerPreferencesSchema>;

export const plannerBoardFilterSchema = z.strictObject({
  boardId: ulidSchema,
});

export type PlannerBoardFilter = z.infer<typeof plannerBoardFilterSchema>;

export const plannerBoardRefSchema = z.strictObject({
  id: ulidSchema,
  title: z.string().max(BOARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
});

export type PlannerBoardRef = z.infer<typeof plannerBoardRefSchema>;

export const plannerListRefSchema = z.strictObject({
  id: ulidSchema,
  title: z.string().max(LIST_TITLE_MAX_LENGTH),
  position: z.string().max(RANK_MAX_LENGTH),
});

export type PlannerListRef = z.infer<typeof plannerListRefSchema>;

export const plannerCardRefSchema = z.strictObject({
  id: ulidSchema,
  title: z.string().max(CARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  startAt: plannerTimestampSchema.nullable(),
  dueAt: plannerTimestampSchema.nullable(),
  completed: z.boolean(),
  canComplete: z.boolean(),
});

export type PlannerCardRef = z.infer<typeof plannerCardRefSchema>;

export const plannerBoardCardRefSchema = plannerCardRefSchema.extend({
  cardNumber: cardNumberSchema,
  /** `KEY-NNNN` for a viewer who may use the board's references, else `null` — `cardReferenceSchema` in `./board`. */
  ref: cardReferenceSchema.nullable(),
});

export type PlannerBoardCardRef = z.infer<typeof plannerBoardCardRefSchema>;

export const plannerBoardCardSchema = z.strictObject({
  source: z.literal('board'),
  card: plannerBoardCardRefSchema.extend({
    dueAt: plannerTimestampSchema,
  }),
  board: plannerBoardRefSchema,
  list: plannerListRefSchema,
  assignedToViewer: z.boolean(),
});

export type PlannerBoardCard = z.infer<typeof plannerBoardCardSchema>;

export const plannerInboxCardSchema = z.strictObject({
  source: z.literal('inbox'),
  card: plannerCardRefSchema.extend({
    dueAt: plannerTimestampSchema,
  }),
});

export type PlannerInboxCard = z.infer<typeof plannerInboxCardSchema>;

export const plannerSchedulableCardSchema = z.discriminatedUnion('source', [
  plannerBoardCardSchema,
  plannerInboxCardSchema,
]);

export type PlannerSchedulableCard = z.infer<typeof plannerSchedulableCardSchema>;

export const plannerLinkedBoardCardSchema = z.strictObject({
  source: z.literal('board'),
  card: plannerBoardCardRefSchema,
  board: plannerBoardRefSchema,
  list: plannerListRefSchema.nullable(),
});

export type PlannerLinkedBoardCard = z.infer<typeof plannerLinkedBoardCardSchema>;

export const plannerLinkedInboxCardSchema = z.strictObject({
  source: z.literal('inbox'),
  card: plannerCardRefSchema,
});

export type PlannerLinkedInboxCard = z.infer<typeof plannerLinkedInboxCardSchema>;

export const plannerLinkedCardDetailsSchema = z.discriminatedUnion('source', [
  plannerLinkedBoardCardSchema,
  plannerLinkedInboxCardSchema,
]);

export type PlannerLinkedCardDetails = z.infer<typeof plannerLinkedCardDetailsSchema>;

export const plannerLinkedCardStateSchema = z.enum([
  'live',
  'completed',
  'inactive',
  'inaccessible',
  'orphaned',
]);

export type PlannerLinkedCardState = z.infer<typeof plannerLinkedCardStateSchema>;

/**
 * `focus_block_cards.id` — the identity of the LINK ROW, not of the card.
 *
 * ⚠ **Not interchangeable with `cardId`, and that is the whole point.** A
 * link's `cardId` is `ON DELETE SET NULL` on the card (see the
 * `focus_block_cards` catalogue), so an `orphaned` link has no card id at
 * all, and `uq_focus_block_cards_focus_block_id_card_id` does not dedupe
 * those — Postgres treats NULLs as distinct, so one block can hold several.
 * Addressing a link by its card id therefore cannot name an orphan, which is
 * why `FR-NOTIF-023`'s edge case ("Deleting the card outright must not strand
 * an event on the connected calendar with no way to remove it from within the
 * product") could not be met before this field existed: the only removal a
 * client could reach for an orphan was deleting the whole focus block, taking
 * every other card linked to it with it.
 *
 * The **mutation** half of this contract has always published this value —
 * `POST /me/planner/focus-blocks` and its siblings answer with
 * `cards: [{ id, cardId, createdAt }]`, where `id` is exactly this. This field
 * restores the read half to agree with it; it is not a new identity.
 *
 * Required rather than optional on purpose: the column is `NOT NULL` and the
 * primary key, so the server always has it, and a client falling back to a
 * missing `linkId` would fall back to the very defect above.
 */
const plannerLinkedCardIdentitySchema = ulidSchema;

export const plannerLinkedCardSchema = z.discriminatedUnion('state', [
  z.strictObject({
    linkId: plannerLinkedCardIdentitySchema,
    state: z.literal('live'),
    cardId: ulidSchema,
    details: plannerLinkedCardDetailsSchema,
  }),
  z.strictObject({
    linkId: plannerLinkedCardIdentitySchema,
    state: z.literal('completed'),
    cardId: ulidSchema,
    details: plannerLinkedCardDetailsSchema,
  }),
  z.strictObject({
    linkId: plannerLinkedCardIdentitySchema,
    state: z.literal('inactive'),
    cardId: ulidSchema,
    details: plannerLinkedCardDetailsSchema,
    reason: z.enum(['archived', 'deleted']),
  }),
  z.strictObject({
    linkId: plannerLinkedCardIdentitySchema,
    state: z.literal('inaccessible'),
    cardId: ulidSchema,
    details: z.null(),
  }),
  z.strictObject({
    linkId: plannerLinkedCardIdentitySchema,
    state: z.literal('orphaned'),
    cardId: ulidSchema.nullable(),
    details: z.null(),
  }),
]);

export type PlannerLinkedCard = z.infer<typeof plannerLinkedCardSchema>;

export const plannerFocusBlockSyncStatusSchema = z.enum([
  'not_configured',
  'pending',
  'synced',
  'failed',
]);

export type PlannerFocusBlockSyncStatus = z.infer<
  typeof plannerFocusBlockSyncStatusSchema
>;

export const plannerFocusBlockCalendarSyncSchema = z.strictObject({
  status: plannerFocusBlockSyncStatusSchema,
  connectedCalendarId: ulidSchema.nullable(),
  externalEventId: z.string().min(1).max(PLANNER_EXTERNAL_REF_MAX_LENGTH).nullable(),
  lastSyncedAt: plannerTimestampSchema.nullable(),
  error: z.string().max(PLANNER_FOCUS_SYNC_ERROR_MAX_LENGTH).nullable(),
});

export type PlannerFocusBlockCalendarSync = z.infer<
  typeof plannerFocusBlockCalendarSyncSchema
>;

export const plannerFocusBlockSchema = z
  .strictObject({
    id: ulidSchema,
    startAt: plannerTimestampSchema,
    endAt: plannerTimestampSchema,
    linkedCards: z.array(plannerLinkedCardSchema),
    calendarSync: plannerFocusBlockCalendarSyncSchema,
    createdAt: plannerTimestampSchema,
    updatedAt: plannerTimestampSchema,
  })
  .refine(isIncreasingBlock, {
    message: 'startAt must be before endAt',
    path: ['endAt'],
  });

export type PlannerFocusBlock = z.infer<typeof plannerFocusBlockSchema>;

export const connectedCalendarProviderSchema = z.enum(['google', 'outlook']);

export type ConnectedCalendarProvider = z.infer<typeof connectedCalendarProviderSchema>;

export const calendarProviderSchema = connectedCalendarProviderSchema;

export type CalendarProvider = ConnectedCalendarProvider;

export const connectedCalendarAccountSchema = z.strictObject({
  id: ulidSchema,
  provider: connectedCalendarProviderSchema,
  externalAccountId: z.string().min(1).max(PLANNER_EXTERNAL_REF_MAX_LENGTH),
  label: z.string().min(1).max(PLANNER_CALENDAR_NAME_MAX_LENGTH).nullable(),
  connectedAt: plannerTimestampSchema,
  updatedAt: plannerTimestampSchema,
});

export type ConnectedCalendarAccount = z.infer<typeof connectedCalendarAccountSchema>;

export const connectedCalendarSchema = z.strictObject({
  id: ulidSchema,
  accountId: ulidSchema,
  provider: connectedCalendarProviderSchema,
  externalCalendarId: z.string().min(1).max(PLANNER_EXTERNAL_REF_MAX_LENGTH),
  name: z.string().min(1).max(PLANNER_CALENDAR_NAME_MAX_LENGTH),
  colour: z.string().min(1).max(PLANNER_CALENDAR_COLOUR_MAX_LENGTH).nullable(),
  displayed: z.boolean(),
  writable: z.boolean(),
  primary: z.boolean(),
  timezone: z.string().min(1).max(255).nullable(),
  createdAt: plannerTimestampSchema,
  updatedAt: plannerTimestampSchema,
});

export type ConnectedCalendar = z.infer<typeof connectedCalendarSchema>;

export const connectedCalendarAccountListSchema = z.strictObject({
  accounts: z.array(connectedCalendarAccountSchema),
  calendars: z.array(connectedCalendarSchema),
});

export type ConnectedCalendarAccountList = z.infer<
  typeof connectedCalendarAccountListSchema
>;

export const calendarAuthorizeBodySchema = z.strictObject({
  externalAccountRef: z
    .string()
    .trim()
    .min(1)
    .max(PLANNER_EXTERNAL_REF_MAX_LENGTH)
    .optional(),
});

export type CalendarAuthorizeBody = z.infer<typeof calendarAuthorizeBodySchema>;

export const calendarAuthorizeResponseSchema = z.strictObject({
  provider: connectedCalendarProviderSchema,
  mode: z.literal('fake'),
  authorizationUrl: z.string().min(1).max(2048),
  state: z.string().min(16).max(256),
});

export type CalendarAuthorizeResponse = z.infer<typeof calendarAuthorizeResponseSchema>;

export const calendarAuthorizeCallbackQuerySchema = z.strictObject({
  state: z.string().min(16).max(256),
  code: z.string().min(1).max(512).optional(),
});

export type CalendarAuthorizeCallbackQuery = z.infer<
  typeof calendarAuthorizeCallbackQuerySchema
>;

export const externalCalendarEventSchema = z
  .strictObject({
    id: z.string().min(1).max(PLANNER_EXTERNAL_REF_MAX_LENGTH),
    accountId: ulidSchema,
    calendarId: ulidSchema,
    provider: connectedCalendarProviderSchema,
    externalEventId: z.string().min(1).max(PLANNER_EXTERNAL_REF_MAX_LENGTH),
    title: z.string().max(PLANNER_EXTERNAL_EVENT_TITLE_MAX_LENGTH),
    startAt: plannerTimestampSchema,
    endAt: plannerTimestampSchema,
    allDay: z.boolean(),
    busy: z.boolean(),
    location: z.string().max(PLANNER_EXTERNAL_EVENT_LOCATION_MAX_LENGTH).nullable(),
  })
  .refine(isIncreasingBlock, {
    message: 'startAt must be before endAt',
    path: ['endAt'],
  });

export type ExternalCalendarEvent = z.infer<typeof externalCalendarEventSchema>;

export const plannerSchema = z.strictObject({
  range: plannerRangeQuerySchema,
  preferences: plannerPreferencesSchema,
  selectedBoardFilters: z.array(plannerBoardFilterSchema),
  schedulableCards: z.array(plannerSchedulableCardSchema),
  focusBlocks: z.array(plannerFocusBlockSchema),
  connectedCalendarAccounts: z
    .array(connectedCalendarAccountSchema)
    .max(PLANNER_CONNECTED_ACCOUNT_LIMIT),
  connectedCalendars: z.array(connectedCalendarSchema),
  externalEvents: z.array(externalCalendarEventSchema),
});

export type Planner = z.infer<typeof plannerSchema>;

export const replacePlannerBoardFiltersBodySchema = z.strictObject({
  boardIds: z.array(ulidSchema).refine(hasUniqueValues, {
    message: 'boardIds must not contain duplicates',
  }),
});

export type ReplacePlannerBoardFiltersBody = z.infer<
  typeof replacePlannerBoardFiltersBodySchema
>;

export const plannerIncludeUnassignedPatchSchema = z.strictObject({
  includeUnassigned: z.boolean(),
});

export type PlannerIncludeUnassignedPatch = z.infer<
  typeof plannerIncludeUnassignedPatchSchema
>;

export const plannerWorkingHoursPatchSchema = plannerWorkingHoursSchema;

export type PlannerWorkingHoursPatch = z.infer<typeof plannerWorkingHoursPatchSchema>;

export const plannerWorkingDaysPatchSchema = z.strictObject({
  workingDays: plannerWorkingDaysSchema,
});

export type PlannerWorkingDaysPatch = z.infer<typeof plannerWorkingDaysPatchSchema>;

export const plannerCalendarDisplayedPatchSchema = z.strictObject({
  displayed: z.boolean(),
});

export type PlannerCalendarDisplayedPatch = z.infer<
  typeof plannerCalendarDisplayedPatchSchema
>;

export const calendarDisplayedBodySchema = plannerCalendarDisplayedPatchSchema;

export type CalendarDisplayedBody = PlannerCalendarDisplayedPatch;

export const plannerFocusDurationMinutesSchema = z
  .number()
  .int()
  .min(PLANNER_MIN_FOCUS_DURATION_MINUTES)
  .max(PLANNER_MAX_FOCUS_DURATION_MINUTES);

export type PlannerFocusDurationMinutes = z.infer<
  typeof plannerFocusDurationMinutesSchema
>;

/*
 * ⚠ **Route-less, and no longer the home of the write half.** This schema is
 * wired to nothing: `POST /me/planner/focus-blocks` declares
 * `plannerFocusBlockCreateBodySchema` (below) and always has, and the two
 * disagree — `durationMinutes` here, `endAt` there.
 *
 * The Planner Suggestions chunk kept it on the grounds that its
 * `connectedCalendarId` "is residual 1's write half and MUST SURVIVE". **That
 * reason expired when this chunk landed**: the live schema below now declares
 * and consumes the field, so the copy here is a duplicate rather than a
 * reservation. It is still not deleted — six route-less B75 siblings stand
 * beside it and retiring them is one deliberate decision about all seven, not
 * a side effect of adopting one field — but a later reader should not read
 * this field as load-bearing. It is not.
 */
export const createPlannerFocusBlockBodySchema = z.strictObject({
  startAt: plannerInstantSchema,
  durationMinutes: plannerFocusDurationMinutesSchema.default(
    PLANNER_DEFAULT_FOCUS_DURATION_MINUTES,
  ),
  cardIds: z
    .array(ulidSchema)
    .max(PLANNER_FOCUS_BLOCK_CARD_LIMIT)
    .refine(hasUniqueValues, { message: 'cardIds must not contain duplicates' })
    .default([]),
  connectedCalendarId: ulidSchema.optional(),
});

export type CreatePlannerFocusBlockBody = z.infer<
  typeof createPlannerFocusBlockBodySchema
>;

export const movePlannerFocusBlockBodySchema = z.strictObject({
  startAt: plannerInstantSchema,
});

export type MovePlannerFocusBlockBody = z.infer<typeof movePlannerFocusBlockBodySchema>;

export const resizePlannerFocusBlockBodySchema = z.strictObject({
  durationMinutes: plannerFocusDurationMinutesSchema,
});

export type ResizePlannerFocusBlockBody = z.infer<
  typeof resizePlannerFocusBlockBodySchema
>;

export const deletePlannerFocusBlockBodySchema = z.strictObject({});

export type DeletePlannerFocusBlockBody = z.infer<
  typeof deletePlannerFocusBlockBodySchema
>;

export const linkPlannerFocusBlockCardBodySchema = z.strictObject({
  cardId: ulidSchema,
});

export type LinkPlannerFocusBlockCardBody = z.infer<
  typeof linkPlannerFocusBlockCardBodySchema
>;

export const unlinkPlannerFocusBlockCardBodySchema = z.strictObject({
  cardId: ulidSchema,
});

export type UnlinkPlannerFocusBlockCardBody = z.infer<
  typeof unlinkPlannerFocusBlockCardBodySchema
>;

export const plannerBusySlotSchema = z
  .strictObject({
    startAt: plannerInstantSchema,
    endAt: plannerInstantSchema,
  })
  .refine(isIncreasingBlock, {
    message: 'endAt must be after startAt',
    path: ['endAt'],
  });

export type PlannerBusySlot = z.infer<typeof plannerBusySlotSchema>;

/*
 * `plannerSuggestionRequestBodySchema` stood here and was wired to nothing.
 * `POST /me/planner/suggestions` has always declared
 * `plannerSuggestionsBodySchema` (below), so this one read to every future
 * author as a shipped request contract that disagreed with the shipped one.
 * Adopting it wholesale would have DROPPED `workingHoursStart`,
 * `workingHoursEnd` and `workingDays` -- fields the web client sends and a
 * browser test asserts -- so the schema is retired and its single
 * requirement-bearing field, `sourceBoardIds` (`FR-NOTIF-025`, "constrain
 * proposals by source board"), moves onto the live schema instead. Its
 * `cardIds` is deliberately not carried over: no requirement asks proposals to
 * be constrained to a card list.
 */

export const plannerSuggestionSchema = z
  .strictObject({
    proposalId: z.string().min(1).max(160),
    startAt: plannerTimestampSchema,
    endAt: plannerTimestampSchema,
    cardIds: z.array(ulidSchema).min(1).max(PLANNER_FOCUS_BLOCK_CARD_LIMIT),
    reason: z.string().max(PLANNER_SUGGESTION_REASON_MAX_LENGTH).nullable().optional(),
  })
  .refine(isIncreasingBlock, {
    message: 'startAt must be before endAt',
    path: ['endAt'],
  });

export type PlannerSuggestion = z.infer<typeof plannerSuggestionSchema>;

/*
 * `plannerSuggestionListSchema` -- `{ proposals: PlannerSuggestion[] }` --
 * stood here with zero references of any kind. `POST /me/planner/suggestions`
 * answers `plannerSuggestionsSchema` (`{ range, suggestions }`) and always
 * has, so this was a second, competing response shape for the same route with
 * nothing to disambiguate it. Retired rather than left to be adopted by
 * accident.
 */

export const plannerFocusBlockCreateBodySchema = z
  .strictObject({
    startAt: plannerInstantSchema,
    endAt: plannerInstantSchema,
    cardIds: z.array(ulidSchema).max(PLANNER_FOCUS_BLOCK_CARD_LIMIT).optional(),
    /**
     * `FR-NOTIF-022` — "Focus time blocks are written to the user's connected
     * calendar so colleagues see the time as busy". This is the WRITE half of
     * the provider-sync seam, and it names **which** connected calendar the
     * block is queued against: `createFocusBlock` inserts one
     * `focus_block_calendar_events` row at `sync_status = 'pending'`, inside
     * the transaction that writes the block itself.
     *
     * ⚠ **This field is NOT the one the Planner Suggestions residual pointed
     * at.** That residual said `createFocusBlock`'s "body schema already
     * declares an unconsumed `connectedCalendarId`". It does not, and did not:
     * the declared-and-unconsumed copy is on
     * `createPlannerFocusBlockBodySchema` above — a route-less B75 schema
     * carrying `durationMinutes` where this one carries `endAt`, so adopting
     * *it* would have broken this route and `apps/web/src/lib/planner.ts` both.
     * The field is declared here instead, and that copy is left exactly as it
     * stands.
     *
     * **Optional, and with no `.default()`.** Omitting it must leave the parse
     * output without the key at all rather than with `undefined`, because that
     * is what keeps the answer byte for byte identical for every shipped
     * client: no row is inserted, and `GET /me/planner` keeps reporting
     * `calendarSync.status: 'not_configured'`. A `.default()` here — even
     * `undefined` — would put the key in the body and invite a later reader to
     * branch on its presence.
     *
     * There is deliberately no "write to every displayed calendar" fallback.
     * `FR-NOTIF-022` says blocks are written to the user's connected calendar
     * and never says which, so a fan-out would be policy the corpus does not
     * state — and it would silently change the answer for every existing user
     * who has a calendar connected.
     */
    connectedCalendarId: ulidSchema.optional(),
  })
  .refine(isIncreasingBlock, {
    message: 'endAt must be after startAt',
    path: ['endAt'],
  });

export type PlannerFocusBlockCreateBody = z.infer<
  typeof plannerFocusBlockCreateBodySchema
>;

export const plannerFocusBlockTimePatchSchema = z
  .strictObject({
    startAt: plannerInstantSchema,
    endAt: plannerInstantSchema,
  })
  .refine(isIncreasingBlock, {
    message: 'endAt must be after startAt',
    path: ['endAt'],
  });

export type PlannerFocusBlockTimePatch = z.infer<typeof plannerFocusBlockTimePatchSchema>;

export const plannerSuggestionsBodySchema = z
  .strictObject({
    from: plannerInstantSchema,
    to: plannerInstantSchema,
    durationMinutes: plannerFocusDurationMinutesSchema.default(
      PLANNER_DEFAULT_FOCUS_DURATION_MINUTES,
    ),
    maxSuggestions: z
      .number()
      .int()
      .min(1)
      .max(PLANNER_SUGGESTION_LIMIT_MAX)
      .default(PLANNER_SUGGESTION_LIMIT_DEFAULT),
    /**
     * `FR-NOTIF-025` -- "constrain proposals by source board". Scoped to THIS
     * request: it overrides the stored `planner_board_filters` selection for
     * one call, exactly as `workingHoursStart` / `workingHoursEnd` /
     * `workingDays` below already override the stored preference. That stored
     * selection is `FR-NOTIF-020`'s clause and filters *the panel*; this one is
     * `FR-NOTIF-025`'s and constrains *proposals*. Two clauses, two scopes.
     *
     * Omitted means "no proposal-scoped constraint" -- the stored filter still
     * applies -- so a client that has never heard of this field gets exactly
     * today's answer.
     *
     * A filter, never a lookup: the ids are ANDed into a query that already
     * carries `isBoardReadableBy(userId)`, so a board that does not exist, one
     * the caller cannot read, a deleted one and a closed one are
     * indistinguishable -- each contributes no rows and the route answers a
     * normal 200. No 404 and no 403 branch is added, because a route that
     * answered 404 for "no such board" and 200 for "a board you cannot read"
     * would be an existence oracle across boards.
     */
    sourceBoardIds: z
      .array(ulidSchema)
      .min(1)
      .max(PLANNER_SUGGESTION_SOURCE_BOARD_LIMIT)
      .refine(hasUniqueValues, {
        message: 'sourceBoardIds must not contain duplicates',
      })
      .optional(),
    workingHoursStart: plannerTimeOfDaySchema.optional(),
    workingHoursEnd: plannerTimeOfDaySchema.optional(),
    workingDays: z.number().int().min(0).max(127).optional(),
    externalEvents: z.array(plannerBusySlotSchema).optional(),
  })
  .refine(isIncreasingRange, increasingRangeIssue)
  .refine(
    (value) =>
      value.workingHoursStart === undefined ||
      value.workingHoursEnd === undefined ||
      secondsSinceMidnight(value.workingHoursStart) <
        secondsSinceMidnight(value.workingHoursEnd),
    {
      message: 'workingHoursEnd must be after workingHoursStart',
      path: ['workingHoursEnd'],
    },
  );

export type PlannerSuggestionsBody = z.infer<typeof plannerSuggestionsBodySchema>;

export const plannerSuggestionsSchema = z.strictObject({
  range: plannerRangeQuerySchema,
  suggestions: z.array(plannerSuggestionSchema),
});

export type PlannerSuggestions = z.infer<typeof plannerSuggestionsSchema>;
